import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

// Simulated progress that increases over time
export const useLoadingProgress = (isLoading, estimatedDuration = 10000) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    if (!isLoading) {
      setProgress(0);
      return;
    }
    
    setProgress(5);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        // Slow down as we approach 90%
        if (prev >= 90) return prev;
        if (prev >= 70) return prev + 0.5;
        if (prev >= 50) return prev + 1;
        return prev + 2;
      });
    }, estimatedDuration / 50);
    
    return () => clearInterval(interval);
  }, [isLoading, estimatedDuration]);
  
  // Jump to 100% when loading completes
  useEffect(() => {
    if (!isLoading && progress > 0) {
      setProgress(100);
      const timeout = setTimeout(() => setProgress(0), 500);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, progress]);
  
  return progress;
};

// Loading card overlay
export const LoadingOverlay = ({ 
  isLoading, 
  message = "Loading...", 
  subMessage = "Please wait while we fetch the latest data",
  progress = null,
  className = ""
}) => {
  const simulatedProgress = useLoadingProgress(isLoading, 15000);
  const displayProgress = progress !== null ? progress : simulatedProgress;
  
  if (!isLoading) return null;
  
  return (
    <div className={`absolute inset-0 bg-background/80 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-lg ${className}`}>
      <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
      <p className="text-sm font-medium mb-1">{message}</p>
      <p className="text-xs text-muted-foreground mb-4">{subMessage}</p>
      <div className="w-48">
        <Progress value={displayProgress} className="h-2" />
        <p className="text-xs text-center mt-1 text-muted-foreground">{Math.round(displayProgress)}%</p>
      </div>
    </div>
  );
};

// Inline loading indicator for smaller areas
export const InlineLoader = ({ 
  isLoading, 
  message = "Loading...",
  showProgress = true
}) => {
  const progress = useLoadingProgress(isLoading, 10000);
  
  if (!isLoading) return null;
  
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg animate-pulse">
      <Loader2 className="w-4 h-4 animate-spin text-primary" />
      <div className="flex-1">
        <p className="text-sm text-muted-foreground">{message}</p>
        {showProgress && (
          <div className="flex items-center gap-2 mt-1">
            <Progress value={progress} className="h-1 flex-1" />
            <span className="text-xs text-muted-foreground w-8">{Math.round(progress)}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Skeleton with progress for data cards
export const DataCardSkeleton = ({ 
  title = "Loading Data",
  lines = 3,
  showProgress = true
}) => {
  const progress = useLoadingProgress(true, 8000);
  
  return (
    <div className="space-y-3 p-4 bg-muted/20 rounded-lg border border-border/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <span className="text-sm font-medium">{title}</span>
        </div>
        {showProgress && (
          <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
        )}
      </div>
      {showProgress && <Progress value={progress} className="h-1" />}
      <div className="space-y-2">
        {[...Array(lines)].map((_, i) => (
          <div 
            key={i} 
            className="h-4 bg-muted rounded animate-pulse"
            style={{ 
              width: `${100 - (i * 15)}%`,
              animationDelay: `${i * 150}ms`
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Loading states for specific operations
export const loadingMessages = {
  marketRegime: {
    message: "Analyzing Market Conditions",
    subMessage: "Scanning S&P 500, FTSE 100, and VIX indicators..."
  },
  macroIntelligence: {
    message: "Gathering Market Intelligence",
    subMessage: "AI is analyzing global economic factors..."
  },
  simpleAdvice: {
    message: "Generating Your Personalized Advice",
    subMessage: "AI is reviewing your portfolios and market conditions..."
  },
  agentDebate: {
    message: "Running Agent Debate",
    subMessage: "Scout, Skeptic, and Orchestrator are analyzing opportunities..."
  },
  syncPrices: {
    message: "Syncing Live Prices",
    subMessage: "Fetching latest market data from exchanges..."
  },
  analyze: {
    message: "Running AI Analysis",
    subMessage: "Evaluating risk, diversification, and opportunities..."
  },
  email: {
    message: "Sending Email",
    subMessage: "Preparing and delivering your briefing..."
  }
};

export default LoadingOverlay;
